import React from 'react';
import { Badge } from '@atoms';
import { PermissionInfoCell, PermissionStatusBadge, PermissionActionButtons } from '@molecules';

export interface Permission {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  resource: string;
  action: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionTableProps {
  permissions: Permission[];
  searchTerm: string;
  resourceFilter: string;
  onEditPermission?: (permission: Permission) => void;
  onDeletePermission?: (permission: Permission) => void;
  isDeleteDisabled?: (permission: Permission) => boolean;
  showActions?: boolean;
}

export const PermissionTable: React.FC<PermissionTableProps> = ({
  permissions,
  searchTerm,
  resourceFilter,
  onEditPermission,
  onDeletePermission,
  isDeleteDisabled,
  showActions = true
}) => {
  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = 
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (permission.description && permission.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesResource = !resourceFilter || permission.resource === resourceFilter;

    return matchesSearch && matchesResource;
  });

  return (
    <div className="bg-background-primary rounded-lg border border-border-primary overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Permission
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Resource
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              {showActions && (
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {filteredPermissions.length === 0 ? (
              <tr>
                <td colSpan={showActions ? 6 : 5} className="px-6 py-8 text-center text-text-secondary">
                  {searchTerm || resourceFilter 
                    ? 'No permissions found matching your filters.' 
                    : 'No permissions found.'
                  }
                </td>
              </tr>
            ) : (
              filteredPermissions.map((permission) => (
                <tr key={permission.id} className="hover:bg-background-secondary">
                  <td className="px-6 py-4">
                    <PermissionInfoCell
                      displayName={permission.displayName}
                      name={permission.name}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="info" size="sm">
                      {permission.resource}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="purple" size="sm">
                      {permission.action}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-text-secondary">
                      {permission.description || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <PermissionStatusBadge isActive={permission.isActive} />
                  </td>
                  {showActions && (
                    <td className="px-6 py-4 text-right">
                      <PermissionActionButtons
                        onEdit={onEditPermission ? () => onEditPermission(permission) : undefined}
                        onDelete={onDeletePermission ? () => onDeletePermission(permission) : undefined}
                        isDeleteDisabled={isDeleteDisabled?.(permission) || false}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
